// InstitutionalSections · datasheet-grade panels that turn the bare
// AccountTypeDetailPage into a serious institutional account spec page.
//
// Renders 8 panels in sequence, each fed by a real backend endpoint:
//   1. KeyMetricsStrip       · 6-tile spec snapshot under the hero
//   2. LivePricingPanel      · top-8 instruments with live bid/ask + tier
//                              commission, hooked into /api/market/quotes
//                              via /api/account/{tier}/snapshot
//   3. SpecificationsTable   · full 20-row datasheet · /api/account/{tier}/specs
//   4. ExecutionQuality      · fill rate / latency / slippage · institutional
//                              routing-disclosure report
//   5. CostOfTradingExample  · concrete £ / $ breakdown for 1 lot EURUSD
//   6. SwapRatesTable        · per-symbol overnight swap · /api/swaps
//   7. PersonasBlock         · 3 institutional personas (who this is for)
//   8. SideBySideComparison  · this tier vs the next 2 in a tight matrix
//
// Visual rules · same as the rest of the site: card-beam shells, accent
// opacities ≤ 14 %, mono numerics with tabular-nums, no neon glows,
// dense editorial layout. Outfit font globally.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Gauge, Banknote, Layers, Crown, Star, Sparkles,
  Activity, TrendingUp, ShieldCheck, Server, Globe, Clock,
  AlertTriangle, CheckCircle2, ArrowRight, Cpu,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- shared helpers ---------- */
const fmtMoney = (n) => `$${Number(n).toLocaleString("en-US")}`;
const fmtPct   = (n) => `${Number(n).toFixed(2)}%`;
const fmtPips  = (n) => `${Number(n).toFixed(1)} pip${Number(n) === 1 ? "" : "s"}`;
const fmtMs    = (n) => `${Number(n).toFixed(1)} ms`;
const tabular  = "font-mono tabular-nums";

const EYEBROW = "font-mono text-[12px] uppercase tracking-[0.22em] text-text-3";

/* Small section wrapper · institutional rhythm · centered on mobile */
function Section({ id, eyebrow, title, accent, lead, children }) {
  return (
    <section className="mb-20 lg:mb-24" id={id} data-testid={id}>
      <div className="mb-7 lg:mb-9 text-center lg:text-left">
        <div className={`${EYEBROW} mb-3`}>{eyebrow}</div>
        <h2 className="font-display text-[1.85rem]/[1.15] lg:text-[2.5rem]/[1.08] font-medium tracking-[-0.02em] text-text-1 max-w-3xl mx-auto lg:mx-0">
          {title} {accent && <span className="text-gradient-brand font-semibold">{accent}</span>}
        </h2>
        {lead && <p className="text-text-3 mt-4 max-w-2xl mx-auto lg:mx-0 text-[14.5px] lg:text-[15px] font-light leading-relaxed">{lead}</p>}
      </div>
      {children}
    </section>
  );
}

/* ============================================================
 * 1. KeyMetricsStrip — 6 numeric tiles right under the hero
 * ============================================================ */
export function KeyMetricsStrip({ specs }) {
  const { t } = useTranslation();
  if (!specs) return null;
  const items = [
    { k: t("institutional.metrics.min", "Min Deposit"),     v: specs.min_deposit_display ?? fmtMoney(specs.min_deposit_usd), sub: t("institutional.metrics.subMin", "Same minimum across all base currencies") },
    { k: t("institutional.metrics.spread", "Typical EURUSD"),  v: specs.typical_spread_display ? `${specs.typical_spread_display} pips` : fmtPips(specs.typical_spread_eurusd_pips), sub: t("institutional.metrics.subSpread", "Last 30-day median spread on this account") },
    { k: t("institutional.metrics.comm", "Commission"),      v: specs.commission_display ?? (specs.commission_per_lot_per_side_usd === 0 ? "$0" : `$${specs.commission_per_lot_per_side_usd}`), sub: t("institutional.metrics.subComm", "Per lot · per side") },
    { k: t("institutional.metrics.lev", "Max Leverage"),    v: specs.max_leverage,                                  sub: t("institutional.metrics.subLev", "Scales up as your account grows") },
    { k: t("institutional.metrics.lot", "Min Lot"),         v: specs.min_lot_display ?? `${specs.min_lot}`,         sub: t("institutional.metrics.subLot", "Micro-lot trading supported") },
    { k: t("institutional.metrics.wd", "Withdrawal"),      v: `${specs.avg_withdrawal_business_days} ${t("institutional.metrics.day", "day")}`, sub: t("institutional.metrics.subWd", "Same-day via local channels") },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4 mb-20 lg:mb-24" data-testid="institutional-key-metrics">
      {items.map((m, i) => (
        <motion.div
          key={m.k}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl p-4 lg:p-5 relative overflow-hidden border"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
        >
          <div className={`${EYEBROW} mb-2`}>{m.k}</div>
          <div className={`font-display text-[22px] lg:text-[26px] font-medium text-text-1 leading-tight tracking-[-0.01em] ${tabular}`}>{m.v}</div>
          <div className="text-text-3 text-[12px] lg:text-[12px] font-light leading-snug mt-1.5">{m.sub}</div>
        </motion.div>
      ))}
    </div>
  );
}

/* ============================================================
 * 2. LivePricingPanel — top-8 live bid/ask with tier commission
 * ============================================================ */
export function LivePricingPanel({ tier }) {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => fetch(`${API}/api/account/${tier}/snapshot`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setSnapshot(d); })
      .catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tier]);

  if (!snapshot) {
    return (
      <Section id="institutional-live-pricing" eyebrow={t("institutional.live.eyebrow", "Live pricing")} title={t("institutional.live.title", "Real spreads · this account")} lead={t("institutional.live.leadLoading", "Connecting to the price hub …")}>
        <div className="rounded-2xl border p-8 text-text-3 text-[13px]" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
          {t("institutional.live.loading", "Loading live quotes …")}
        </div>
      </Section>
    );
  }

  const isLive = snapshot.feed_status === "connected" || snapshot.feed_status === "live" || snapshot.feed_status === "fallback";
  return (
    <Section
      id="institutional-live-pricing"
      eyebrow={t("institutional.live.eyebrowFull", "Live pricing · this account")}
      title={t("institutional.live.titleFull", "Real spreads")}
      accent={t("institutional.live.accent", "right now")}
      lead={t("institutional.live.leadFull", "The eight most-traded instruments on this account, with the bid/ask and commission you'd pay as a client today. Updates every 4 seconds.")}
    >
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
        {/* Header strip */}
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--brand) 3%, var(--surface-2))" }}>
          <div className="flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5">
              {isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--brand)" }} />}
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: isLive ? "var(--brand)" : "var(--text-3)" }} />
            </span>
            <span className={`${EYEBROW}`}>{isLive ? t("institutional.live.liveConn", "LIVE · price hub connected") : t("institutional.live.connecting", "Connecting…")}</span>
          </div>
          <span className={`${EYEBROW}`}>{t("institutional.live.updated", "Updated · {{time}}", { time: new Date(snapshot.as_of).toLocaleTimeString() })}</span>
        </div>
        {/* Column heads */}
        <div className="hidden lg:grid grid-cols-12 gap-2 px-5 py-2.5 text-[12px] text-text-3 uppercase tracking-[0.16em] font-mono border-b" style={{ borderColor: "var(--line)" }}>
          <div className="col-span-3">{t("institutional.live.symbol", "Symbol")}</div>
          <div className="col-span-2 text-right">{t("institutional.live.bid", "Bid")}</div>
          <div className="col-span-2 text-right">{t("institutional.live.ask", "Ask")}</div>
          <div className="col-span-2 text-right">{t("institutional.live.spread", "Spread")}</div>
          <div className="col-span-3 text-right">{t("institutional.live.commRT", "Round-turn commission")}</div>
        </div>
        {/* Rows */}
        {snapshot.quotes.map((q, i) => (
          <div
            key={q.symbol}
            className={`grid grid-cols-12 gap-2 px-5 py-3 items-center ${i < snapshot.quotes.length - 1 ? "border-b" : ""}`}
            style={{ borderColor: "var(--line)" }}
            data-testid={`live-pricing-row-${q.symbol}`}
          >
            <div className="col-span-6 lg:col-span-3 flex items-center gap-2">
              <span className={`${tabular} text-[13px] font-medium text-text-1`}>{q.symbol}</span>
              <span className="hidden lg:inline text-text-3 text-[12px] font-light truncate">{q.name}</span>
            </div>
            <div className={`hidden lg:block col-span-2 text-right ${tabular} text-[13px] text-text-2`}>{q.bid?.toFixed(q.bid > 100 ? 2 : 5)}</div>
            <div className={`hidden lg:block col-span-2 text-right ${tabular} text-[13px] text-text-2`}>{q.ask?.toFixed(q.ask > 100 ? 2 : 5)}</div>
            <div className="col-span-3 lg:col-span-2 text-right">
              <span className={`${tabular} text-[12px] px-2 py-0.5 rounded-md`} style={{ color: "var(--brand)", background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
                {q.spread?.toFixed(1)} {t("institutional.live.pip", "pip")}
              </span>
            </div>
            <div className={`col-span-3 lg:col-span-3 text-right ${tabular} text-[12.5px] text-text-2`}>
              {q.commission === 0
                ? t("institutional.live.spreadOnly", "$0.00 · spread-only")
                : (
                  <>
                    ${(q.commission * 2).toFixed(2)} {t("institutional.live.perLot", "/ lot")}
                    <span className="block text-[12px] text-text-3 font-light mt-0.5">
                      ${q.commission.toFixed(2)} {t("institutional.live.in", "in")} · ${q.commission.toFixed(2)} {t("institutional.live.out", "out")}
                    </span>
                  </>
                )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================
 * 3. SpecificationsTable — 20-row datasheet
 * ============================================================ */
export function SpecificationsTable({ specs }) {
  const { t } = useTranslation();
  if (!specs) return null;
  // Group rows into 3 thematic columns. Each row: [label, value, optional accent flag].
  const yes = <span className={`${tabular} text-[12px] inline-flex items-center gap-1.5`} style={{ color: "#54d97c" }}><CheckCircle2 size={12} strokeWidth={2.4} />{t("institutional.specs.yes", "Yes")}</span>;
  const no  = <span className={`${tabular} text-[12px] text-text-3`}>{t("institutional.specs.no", "No")}</span>;

  const accountRows = [
    [t("institutional.specs.r_label",     "Account label"),       specs.label],
    [t("institutional.specs.r_exec",      "Execution model"),     specs.execution_model],
    [t("institutional.specs.r_serverLoc", "Server location"),     specs.server_location],
    [t("institutional.specs.r_serverLat", "Server latency (LD4)"), fmtMs(specs.server_latency_ld4_ms)],
    [t("institutional.specs.r_mt5Build",  "MT5 build minimum"),   specs.mt5_build],
    [t("institutional.specs.r_baseCcy",   "Base currencies"),     specs.base_currencies.join(" · ")],
    [t("institutional.specs.r_instr",     "Instruments available"), String(specs.instruments)],
  ];
  const tradingRows = [
    [t("institutional.specs.r_minDep",    "Min deposit"),         specs.min_deposit_display ?? fmtMoney(specs.min_deposit_usd)],
    [t("institutional.specs.r_typSpread", "Typical EURUSD spread"), specs.typical_spread_display ? `${specs.typical_spread_display} pips` : fmtPips(specs.typical_spread_eurusd_pips)],
    [t("institutional.specs.r_commPS",    "Commission (per side)"), specs.commission_display ?? (specs.commission_per_lot_per_side_usd === 0 ? "$0 · spread only" : `$${specs.commission_per_lot_per_side_usd}`)],
    [t("institutional.specs.r_maxLev",    "Max leverage"),        specs.max_leverage],
    [t("institutional.specs.r_minPos",    "Min position size"),   specs.min_lot_display ? `${specs.min_lot_display} ${t("institutional.specs.lot", "lot")}` : `${specs.min_lot} ${t("institutional.specs.lot", "lot")}`],
    [t("institutional.specs.r_maxPos",    "Max position size"),   `${specs.max_lot} ${t("institutional.specs.lot", "lot")}`],
    [t("institutional.specs.r_maxOpen",   "Max open positions"),  String(specs.max_open_positions)],
    [t("institutional.specs.r_swap",      "Swap type"),           specs.swap_type],
  ];
  const safetyRows = [
    [t("institutional.specs.r_neg",        "Negative balance protection"), specs.negative_balance_protection ? yes : no],
    [t("institutional.specs.r_marginCall", "Margin call level"),  `${specs.margin_call_pct}%`],
    [t("institutional.specs.r_stopOut",    "Stop-out level"),     `${specs.stop_out_pct}%`],
    [t("institutional.specs.r_hedge",      "Hedging allowed"),    specs.hedging_allowed ? yes : no],
    [t("institutional.specs.r_ea",         "EA / algos allowed"), specs.ea_allowed ? yes : no],
    [t("institutional.specs.r_scalp",      "Scalping allowed"),   specs.scalping_allowed ? yes : no],
    [t("institutional.specs.r_swapFree",   "Swap-free eligible"), specs.swap_free_eligible ? yes : no],
    [t("institutional.specs.r_depMet",     "Deposit methods"),    `${specs.deposit_methods} ${t("institutional.specs.channels", "channels")}`],
    [t("institutional.specs.r_wdTime",     "Withdrawal time"),    `${specs.avg_withdrawal_business_days} ${specs.avg_withdrawal_business_days === 1 ? t("institutional.specs.businessDay", "business day") : t("institutional.specs.businessDays", "business days")}`],
  ];

  const renderTable = (label, rows, Icon) => (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b" style={{ borderColor: "var(--line)", background: "color-mix(in srgb, var(--brand) 3%, var(--surface-2))" }}>
        <Icon size={14} strokeWidth={1.8} className="text-brand" />
        <span className={`${EYEBROW}`}>{label}</span>
      </div>
      <dl>
        {rows.map(([k, v], i) => (
          <div
            key={k}
            className={`flex items-baseline justify-between gap-3 px-5 py-3 ${i < rows.length - 1 ? "border-b" : ""}`}
            style={{ borderColor: "var(--line)" }}
          >
            <dt className="text-text-3 text-[12.5px] leading-snug">{k}</dt>
            <dd className={`text-text-1 text-[13px] font-medium text-right ${tabular} break-words max-w-[55%]`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <Section
      id="institutional-specs"
      eyebrow={t("institutional.specs.eyebrow", "Full datasheet")}
      title={t("institutional.specs.title", "Account specifications")}
      lead={t("institutional.specs.lead", "Every parameter, transparent and in plain numbers. This is the same datasheet our institutional clients receive in their onboarding pack.")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        {renderTable(t("institutional.specs.groupAcct",    "Account & execution"), accountRows, Server)}
        {renderTable(t("institutional.specs.groupTrading", "Trading conditions"),  tradingRows, TrendingUp)}
        {renderTable(t("institutional.specs.groupRisk",    "Risk & operations"),   safetyRows,   ShieldCheck)}
      </div>
    </Section>
  );
}

/* ============================================================
 * 4. ExecutionQuality — fill rate / latency / slippage
 * ============================================================ */
export function ExecutionQuality({ tier }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/account/${tier}/execution-quality`).then(r => r.json()).then(setStats).catch(() => {});
  }, [tier]);
  if (!stats) return null;

  const tiles = [
    { k: t("institutional.exec.fill", "Fill rate · at or better"), v: fmtPct(stats.fill_rate_at_or_better_pct), Icon: TrendingUp, sub: t("institutional.exec.subFill", "Orders filled at requested price or better") },
    { k: t("institutional.exec.lat",  "Avg execution latency"),    v: fmtMs(stats.avg_execution_latency_ms),    Icon: Activity,   sub: t("institutional.exec.subLat",  "Order acknowledgement to fill") },
    { k: t("institutional.exec.slip", "Median slippage"),          v: fmtPips(stats.median_slippage_pips),       Icon: Gauge,      sub: t("institutional.exec.subSlip", "On market orders during normal liquidity") },
    { k: t("institutional.exec.rej",  "Order rejections"),         v: fmtPct(stats.rejected_orders_pct),         Icon: AlertTriangle, sub: t("institutional.exec.subRej", "Of all orders submitted") },
    { k: t("institutional.exec.monthly", "Monthly orders"),        v: stats.monthly_orders_filled.toLocaleString("en-US"), Icon: Layers, sub: t("institutional.exec.subMonthly", "Total orders filled · this account") },
    { k: t("institutional.exec.uptime",  "Platform uptime"),       v: fmtPct(stats.uptime_pct),                  Icon: Clock,      sub: t("institutional.exec.subUptime",  "Last 30 days, MT5 servers") },
  ];

  return (
    <Section
      id="institutional-execution-quality"
      eyebrow={t("institutional.exec.eyebrow", "Execution quality · {{as_of}}", { as_of: stats.as_of })}
      title={t("institutional.exec.title", "Routing disclosure")}
      accent={t("institutional.exec.accent", "(real numbers)")}
      lead={t("institutional.exec.lead", "Institutional brokers publish their routing performance every month. Here is ours · monthly fills, latency to LD4, slippage and uptime, broken out per account.")}
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {tiles.map((t, i) => (
          <motion.div
            key={t.k}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04 }}
            className="rounded-2xl p-5 border"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <div className="flex items-center justify-between mb-3.5">
              <span className={EYEBROW}>{t.k}</span>
              <t.Icon size={14} strokeWidth={1.7} className="text-brand opacity-70" />
            </div>
            <div className={`font-display text-[28px] lg:text-[32px] font-medium text-text-1 leading-none tracking-[-0.02em] ${tabular}`}>{t.v}</div>
            <p className="text-text-3 text-[12px] font-light mt-3 leading-snug">{t.sub}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================
 * 5. CostOfTradingExample — concrete $ breakdown for 1 lot EURUSD
 * ============================================================ */
export function CostOfTradingExample({ specs }) {
  const { t } = useTranslation();
  if (!specs) return null;
  // 1 lot = 100,000 EUR. EURUSD pip value at $10 / pip.
  const spreadPips    = specs.typical_spread_eurusd_pips;
  const spreadCostUsd = spreadPips * 10; // pip value at 100k EUR
  const commPerSide   = specs.commission_per_lot_per_side_usd;
  const totalCommUsd  = commPerSide * 2;
  const totalCostUsd  = spreadCostUsd + totalCommUsd;

  return (
    <Section
      id="institutional-cost-example"
      eyebrow={t("institutional.cost.eyebrow", "Cost transparency")}
      title={t("institutional.cost.title", "What does it cost to trade")}
      accent={t("institutional.cost.accent", "1 lot of EURUSD?")}
      lead={t("institutional.cost.lead", "Concrete numbers · no surprises. Spread cost + commission, calculated from the typical conditions on this account.")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Breakdown */}
        <div className="lg:col-span-7 rounded-2xl border p-6 lg:p-7" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
          <div className={`${EYEBROW} mb-4`}>{t("institutional.cost.breakdown", "Round-turn breakdown · 1 lot EURUSD")}</div>
          <dl className="space-y-3.5">
            <div className="flex items-baseline justify-between gap-3 pb-3 border-b" style={{ borderColor: "var(--line)" }}>
              <dt className="text-text-2 text-[13px]">{t("institutional.cost.spreadRow", "Spread (typical {{pips}} pip × $10/pip)", { pips: spreadPips.toFixed(1) })}</dt>
              <dd className={`${tabular} text-text-1 text-[14px] font-medium`}>${spreadCostUsd.toFixed(2)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 pb-3 border-b" style={{ borderColor: "var(--line)" }}>
              <dt className="text-text-2 text-[13px]">{t("institutional.cost.commIn",  "Commission (in · ${{amount}})",  { amount: commPerSide.toFixed(2) })}</dt>
              <dd className={`${tabular} text-text-1 text-[14px] font-medium`}>${commPerSide.toFixed(2)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 pb-3 border-b" style={{ borderColor: "var(--line)" }}>
              <dt className="text-text-2 text-[13px]">{t("institutional.cost.commOut", "Commission (out · ${{amount}})", { amount: commPerSide.toFixed(2) })}</dt>
              <dd className={`${tabular} text-text-1 text-[14px] font-medium`}>${commPerSide.toFixed(2)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 pt-2">
              <dt className="text-text-1 text-[14px] font-medium">{t("institutional.cost.total", "Total round-turn cost")}</dt>
              <dd className={`${tabular} font-display text-[26px] font-semibold`} style={{ color: "var(--brand)" }}>${totalCostUsd.toFixed(2)}</dd>
            </div>
          </dl>
          <p className="text-text-3 text-[12px] font-light mt-5 leading-snug">
            {t("institutional.cost.note", "Pip value of $10/pip assumes USD account base and 1 standard lot (100,000 EUR). Spread is based on the last 30-day median during liquid London/NY overlap. Costs scale linearly · 0.10 lot = ${{tenth}}.", { tenth: (totalCostUsd / 10).toFixed(2) })}
          </p>
        </div>
        {/* Comparison vs higher-tier */}
        <div className="lg:col-span-5 rounded-2xl border p-6 lg:p-7" style={{ borderColor: "var(--line-strong)", background: "color-mix(in srgb, var(--brand) 3%, var(--surface))" }}>
          <div className={`${EYEBROW} mb-3`}>{t("institutional.cost.compareEyebrow", "Compare to other accounts")}</div>
          <p className="text-text-3 text-[13px] font-light leading-relaxed mb-5">
            {t("institutional.cost.compareBody", "Higher tiers swap the spread cost for a small per-lot commission. The break-even point sits at roughly 8-10 round-turns per month — past that point, Premier or Elite saves you money.")}
          </p>
          <Link
            to="/account-comparison"
            data-testid="cost-compare-cta"
            className="inline-flex items-center gap-2 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors"
            style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)", border: "1px solid color-mix(in srgb, var(--brand) 28%, transparent)" }}
          >
            {t("institutional.cost.compareCta", "See full tier comparison")}
            <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
 * 6. SwapRatesTable — per-symbol overnight swap
 * ============================================================ */
export function SwapRatesTable({ tier }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/swaps?tier=${tier}`).then(r => r.json()).then(setData).catch(() => {});
  }, [tier]);
  if (!data) return null;

  return (
    <Section
      id="institutional-swap-rates"
      eyebrow={t("institutional.swap.eyebrow", "Swap rates · refreshed {{as_of}}", { as_of: data.as_of })}
      title={t("institutional.swap.title", "Overnight financing")}
      lead={t("institutional.swap.lead", "Per-symbol swap rates for the top 12 instruments, applied at 00:00 server time on open positions. Wednesday rollover charges triple swap on most pairs.")}
    >
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
        <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 text-[12px] uppercase tracking-[0.16em] font-mono text-text-3 border-b" style={{ borderColor: "var(--line)" }}>
          <div className="col-span-4">{t("institutional.swap.symbol", "Symbol")}</div>
          <div className="col-span-4 text-right">{t("institutional.swap.long",  "Long (per lot · per night)")}</div>
          <div className="col-span-4 text-right">{t("institutional.swap.short", "Short (per lot · per night)")}</div>
        </div>
        {data.swaps.map((row, i) => (
          <div key={row.symbol} className={`grid grid-cols-12 gap-2 px-5 py-3 ${i < data.swaps.length - 1 ? "border-b" : ""}`} style={{ borderColor: "var(--line)" }}>
            <div className={`col-span-4 ${tabular} text-[13px] font-medium text-text-1`}>{row.symbol}</div>
            <div className={`col-span-4 text-right ${tabular} text-[13px]`} style={{ color: row.long < 0 ? "#fb7185" : "#54d97c" }}>{row.long > 0 ? "+" : ""}{row.long.toFixed(2)}</div>
            <div className={`col-span-4 text-right ${tabular} text-[13px]`} style={{ color: row.short < 0 ? "#fb7185" : "#54d97c" }}>{row.short > 0 ? "+" : ""}{row.short.toFixed(2)}</div>
          </div>
        ))}
      </div>
      <p className="text-text-3 text-[12px] font-light mt-3 leading-snug">{data.notes}</p>
    </Section>
  );
}

/* ============================================================
 * 7. PersonasBlock — who this account is for
 * ============================================================ */
export function PersonasBlock({ specs }) {
  const { t } = useTranslation();
  if (!specs?.personas) return null;
  return (
    <Section
      id="institutional-personas"
      eyebrow={t("institutional.personas.eyebrow", "Account fit")}
      title={t("institutional.personas.title", "Who this account is for")}
      lead={t("institutional.personas.lead", "We don't make a single account for every trader. Here are the three profiles where this account delivers the most value.")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        {specs.personas.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.07 }}
            className="rounded-2xl p-6 border"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className={`${tabular} text-[12px] uppercase tracking-[0.22em]`} style={{ color: "var(--brand)" }}>{t("institutional.personas.profile", "Profile · {{n}}", { n: String(i + 1).padStart(2, "0") })}</span>
              <span className="flex-1 h-px" style={{ background: "var(--line)" }} />
            </div>
            <p className="text-text-2 text-[14px] font-light leading-relaxed">{p}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================
 * 8. SideBySideComparison — this tier vs the next 2
 * ============================================================ */
const TIER_ORDER = ["classic", "premier", "elite"];
const TIER_ICON = { classic: Star, premier: Crown, elite: Sparkles };

export function SideBySideComparison({ tier }) {
  const { t } = useTranslation();
  const [specsByTier, setSpecsByTier] = useState({});
  useEffect(() => {
    Promise.all(TIER_ORDER.map(t => fetch(`${API}/api/account/${t}/specs`).then(r => r.json())))
      .then(arr => {
        const m = {};
        TIER_ORDER.forEach((t, i) => { m[t] = arr[i]; });
        setSpecsByTier(m);
      })
      .catch(() => {});
  }, []);

  const allLoaded = TIER_ORDER.every(t => specsByTier[t]);
  if (!allLoaded) return null;

  const compareRows = [
    { k: t("institutional.compare.r_minDep", "Min deposit"),          field: s => s.min_deposit_display ?? fmtMoney(s.min_deposit_usd) },
    { k: t("institutional.compare.r_typ",    "Typical EURUSD"),       field: s => s.typical_spread_display ? `${s.typical_spread_display} pips` : fmtPips(s.typical_spread_eurusd_pips) },
    { k: t("institutional.compare.r_comm",   "Commission · per side"), field: s => s.commission_display ?? (s.commission_per_lot_per_side_usd === 0 ? "$0" : `$${s.commission_per_lot_per_side_usd}`) },
    { k: t("institutional.compare.r_lev",    "Max leverage"),          field: s => s.max_leverage },
    { k: t("institutional.compare.r_exec",   "Execution"),             field: s => s.execution_model },
    { k: t("institutional.compare.r_lat",    "LD4 latency"),           field: s => fmtMs(s.server_latency_ld4_ms) },
    { k: t("institutional.compare.r_swap",   "Swap-free option"),      field: s => s.swap_free_eligible ? t("institutional.compare.yes", "Yes") : t("institutional.compare.no", "No") },
  ];

  return (
    <Section
      id="institutional-comparison"
      eyebrow={t("institutional.compare.eyebrow", "Side by side")}
      title={t("institutional.compare.title", "How this account compares")}
      lead={t("institutional.compare.lead", "A tight matrix against the other two accounts · scroll horizontally on narrow screens.")}
    >
      <div className="rounded-2xl border overflow-x-auto" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
        <table className="min-w-full">
          <thead>
            <tr style={{ background: "color-mix(in srgb, var(--brand) 3%, var(--surface-2))" }}>
              <th className={`${EYEBROW} text-left px-5 py-4 font-normal`}>{t("institutional.compare.specification", "Specification")}</th>
              {TIER_ORDER.map(tk => {
                const Icon = TIER_ICON[tk];
                const isCurrent = tk === tier;
                return (
                  <th key={tk} className={`${EYEBROW} text-left px-5 py-4 font-normal min-w-[160px]`} style={isCurrent ? { color: "var(--brand)" } : {}}>
                    <div className="flex items-center gap-2">
                      <Icon size={13} strokeWidth={1.8} className={isCurrent ? "text-brand" : "text-text-3"} />
                      <span className="text-text-1 font-display text-[14px] font-medium normal-case tracking-normal">
                        {specsByTier[tk].label}
                      </span>
                      {isCurrent && <span className="text-[12px] uppercase tracking-[0.22em] px-1.5 py-0.5 rounded font-mono" style={{ background: "color-mix(in srgb, var(--brand) 14%, transparent)", color: "var(--brand)" }}>{t("institutional.compare.thisTier", "this tier")}</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {compareRows.map((row, i) => (
              <tr key={row.k} className={i < compareRows.length - 1 ? "border-b" : ""} style={{ borderColor: "var(--line)" }}>
                <td className="px-5 py-3.5 text-text-3 text-[12.5px]">{row.k}</td>
                {TIER_ORDER.map(tk => {
                  const isCurrent = tk === tier;
                  return (
                    <td key={tk} className={`${tabular} px-5 py-3.5 text-[13px] ${isCurrent ? "font-medium" : "font-light"}`} style={{ color: isCurrent ? "var(--text-1)" : "var(--text-2)" }}>
                      {row.field(specsByTier[tk])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ============================================================
 * 9. RiskRegulatoryBlock — required disclosure
 * ============================================================ */
export function RiskRegulatoryBlock({ specs }) {
  const { t } = useTranslation();
  if (!specs) return null;
  return (
    <Section
      id="institutional-risk"
      eyebrow={t("institutional.risk.eyebrow", "Required disclosure")}
      title={t("institutional.risk.title", "Risk & regulation")}
      lead={t("institutional.risk.lead", "Trading leveraged instruments carries substantial risk. Read the disclosure below before opening an account.")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 rounded-2xl border p-6 lg:p-7" style={{ borderColor: "color-mix(in srgb, #fb7185 28%, var(--line))", background: "color-mix(in srgb, #fb7185 3%, var(--surface))" }}>
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={18} className="shrink-0" style={{ color: "#fb7185" }} strokeWidth={1.8} />
            <div className={`${EYEBROW}`} style={{ color: "#fb7185" }}>{t("institutional.risk.warningEyebrow", "Risk warning")}</div>
          </div>
          <p className="text-text-2 text-[13.5px] font-light leading-relaxed">
            {t("institutional.risk.body", "CFDs and leveraged FX are complex instruments and come with a high risk of losing money rapidly due to leverage. A high percentage of retail investor accounts lose money when trading these products. You should consider whether you understand how these products work and whether you can afford to take the high risk of losing your money.")}
            {specs.negative_balance_protection && t("institutional.risk.negProt", " Negative balance protection limits your loss to the funds deposited on this account.")}
          </p>
        </div>
        <div className="lg:col-span-5 rounded-2xl border p-6 lg:p-7" style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}>
          <div className={`${EYEBROW} mb-3 flex items-center gap-2`}>
            <Globe size={12} className="text-brand" strokeWidth={1.8} /> {t("institutional.risk.regulators", "Regulators")}
          </div>
          <ul className="space-y-2.5">
            {specs.regulators.map(r => (
              <li key={r.license_no} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md grid place-items-center font-display text-[12px] font-bold shrink-0" style={{ background: "#003D7B", color: "#fff", letterSpacing: "0.5px" }}>{r.name}</div>
                <div className="leading-tight">
                  <div className="text-text-1 text-[13px] font-medium">{r.name} · {r.jurisdiction}</div>
                  <div className={`${tabular} text-text-3 text-[12px] mt-0.5`}>{t("institutional.risk.license", "License {{no}}", { no: r.license_no })}</div>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/legal/risk" data-testid="institutional-risk-cta" className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand hover:underline">
            {t("institutional.risk.fullDisclosure", "Read full risk disclosure")}
            <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
 * Top-level orchestrator · drop into the page body.
 * ============================================================ */
export default function InstitutionalSections({ tier }) {
  const { t } = useTranslation();
  const [specs, setSpecs] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/account/${tier}/specs`).then(r => r.json()).then(setSpecs).catch(() => {});
  }, [tier]);

  if (!specs) {
    return (
      <div className="mb-24 rounded-2xl border p-8 text-text-3 text-[13px]" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
        {t("institutional.loading", "Loading account specifications …")}
      </div>
    );
  }

  return (
    <>
      <KeyMetricsStrip specs={specs} />
      <LivePricingPanel tier={tier} />
      <SpecificationsTable specs={specs} />
      <ExecutionQuality tier={tier} />
      <CostOfTradingExample specs={specs} />
      <SwapRatesTable tier={tier} />
      <PersonasBlock specs={specs} />
      <SideBySideComparison tier={tier} />
      <RiskRegulatoryBlock specs={specs} />
    </>
  );
}
